import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { FinancialModelWizard } from '@/components/financial-model/FinancialModelWizard';
import { FinancialModelDetailPanel } from '@/components/financial-model/FinancialModelDetailPanel';
import { ConfirmModal } from '@/components/shared/ConfirmModal';
import { SubscriptionLimitBanner } from '@/components/shared/SubscriptionLimitBanner';
import { AiItemsList } from '@/components/shared/AiItemsList';
import { GenerationProgress } from '@/components/shared/GenerationProgress';
import { useAiPagePermissions } from '@/hooks/useAiPagePermissions';
import { projectsApi, financialModelsApi } from '@/utils/api-pb';
import type { Project } from '@/utils/pocketbase';
import type { FinancialModelRecord, FinancialModelInputs } from '@/types/financial-model';
import { calculateFinancialModel } from '@/utils/financial-calculations';
import { exportFinancialModelToCsv, exportFinancialModelToPdf } from '@/utils/financial-export';

import { useTranslation } from 'react-i18next';
const FREE_FINANCIAL_MODEL_LIMIT = 0;

export default function FinancialModelPage() {
  const { t, i18n } = useTranslation('financial-model');
  const [searchParams] = useSearchParams();
  // Data state
  const [projects, setProjects] = useState<Project[]>([]);
  const [financialModels, setFinancialModels] = useState<FinancialModelRecord[]>([]);
  const [selectedModel, setSelectedModel] = useState<FinancialModelRecord | null>(null);

  // UI state
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [editingModel, setEditingModel] = useState<FinancialModelRecord | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<string | null>(null);

  // Permissions
  const { isFree, isBlockedForBasic, canCreateNew, canEdit, canDelete } = useAiPagePermissions(
    financialModels.length,
    FREE_FINANCIAL_MODEL_LIMIT,
    'financialModel',
  );

  // Tooltip state
  const [tooltipVisible, setTooltipVisible] = useState<{ id: string } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-select model (prefer recordId from query param, fallback to first)
  useEffect(() => {
    if (financialModels.length > 0 && !selectedModel) {
      const targetId = searchParams.get('recordId');
      const target = targetId ? financialModels.find(m => m.id === targetId) : null;
      setSelectedModel(target ?? financialModels[0] ?? null);
    }
  }, [financialModels, selectedModel, searchParams]);

  const loadData = async () => {
    try {
      setIsInitialLoading(true);
      const results = await Promise.allSettled([
        projectsApi.list(),
        financialModelsApi.list(),
      ]);

      if (results[0]?.status === 'fulfilled') setProjects(results[0].value);
      else console.error('Error loading projects:', results[0]?.reason);

      if (results[1]?.status === 'fulfilled') setFinancialModels(results[1].value);
      else console.error('Error loading financial models:', results[1]?.reason);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleCreateModel = async (inputs: FinancialModelInputs, projectId: string) => {
    setIsCreating(true);
    try {
      // Calculate on frontend
      const calculatedData = calculateFinancialModel(inputs);

      // Save to PocketBase
      const record = await financialModelsApi.create({
        project: projectId,
        model_name: inputs.modelName,
        model_type: inputs.modelType,
        input_data: inputs,
        calculated_data: calculatedData,
      });

      setShowWizard(false);
      await loadData();

      // Select the new model
      setSelectedModel(record);
      toast.success(t('toast.created'));
    } catch (error) {
      console.error('Error creating financial model:', error);
      toast.error(error instanceof Error ? error.message : t('toast.createError'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleRequestAi = async () => {
    if (!selectedModel) return;
    setIsAiLoading(true);
    try {
      const updated = await financialModelsApi.requestAiRecommendations(selectedModel.id);
      setSelectedModel(updated);
      await loadData();
      toast.success(t('toast.aiReady'));
    } catch (error) {
      console.error('Error requesting AI recommendations:', error);
      toast.error(error instanceof Error ? error.message : t('toast.aiError'));
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await financialModelsApi.delete(id);
      setShowDeleteConfirm(false);
      setModelToDelete(null);
      if (selectedModel?.id === id) {
        setSelectedModel(null);
      }
      await loadData();
      toast.success(t('toast.deleted'));
    } catch (error) {
      console.error('Error deleting financial model:', error);
      toast.error(t('toast.deleteError'));
    }
  };

  const handleExportPdf = async () => {
    if (!selectedModel) return;
    try {
      await exportFinancialModelToPdf(selectedModel, i18n.language);
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(t('toast.pdfError'));
    }
  };

  const handleExportCsv = () => {
    if (!selectedModel) return;
    try {
      exportFinancialModelToCsv(selectedModel);
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error(t('toast.csvError'));
    }
  };

  const getModelTitle = (model: FinancialModelRecord): string => {
    return model.model_name;
  };

  const getModelSubtitle = (model: FinancialModelRecord): string => {
    if (model.model_type === 'saas') return t('wizard.modelSaas.title');
    if (model.model_type === 'one_time') return t('wizard.modelOneTime.title');
    if (model.model_type === 'consulting') return t('wizard.modelConsulting.title');
    return model.model_type;
  };

  if (!isInitialLoading && (isFree || isBlockedForBasic)) {
    return (
      <><PageHeader title={t('title')} description={t('description')} />
        <SubscriptionLimitBanner
          title={isBlockedForBasic ? t('paidBanner.titlePremium') : t('paidBanner.title')}
          description={isBlockedForBasic ? t('paidBanner.descriptionPremium') : t('paidBanner.description')}
        />
      </>
    );
  }

  return (
    <>
      {isInitialLoading ? (
        <div className="flex flex-col items-center justify-center gap-[16px] py-[80px]">
          <h2 className="font-manrope font-bold text-[32px] leading-[40px] text-[#0d0d0d] text-center">
            {t('title')}
          </h2>
          <p className="font-segoe text-[16px] leading-[24px] text-[#565858] text-center">
            {t('loading')}
          </p>
          <div className="w-[32px] h-[32px] border-2 border-[#2197f1] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : financialModels.length === 0 ? (
        <>
          <PageHeader
            title={t('title')}
            description={t('description')}
          />
          <div className="flex flex-col gap-[24px]">
            <div className="font-segoe text-[16px] leading-[24px] text-[#565858] max-w-[769px]">
              <p>
                {t('empty.text')}
              </p>
            </div>

            <button
              onClick={() => { if (canCreateNew) { setEditingModel(null); setShowWizard(true); } }}
              disabled={!canCreateNew}
              onMouseEnter={() => !canCreateNew && setTooltipVisible({ id: 'create-empty' })}
              onMouseLeave={() => setTooltipVisible(null)}
              className="relative inline-block"
            >
              <div className={`${canCreateNew
                ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
              } rounded-[16px] px-[24px] py-[12px] transition-colors inline-block`}>
                <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                  {t('newButton')}
                </p>
              </div>

              {tooltipVisible?.id === 'create-empty' && !canCreateNew && (
                <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                  <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                    {t('tooltip.paidOnly')}
                  </p>
                </div>
              )}
            </button>
          </div>
        </>
      ) : (
        <div className="flex flex-col lg:flex-row gap-[24px]">
          {/* Left Sidebar */}
          <div className="flex flex-col gap-[8px] w-full lg:w-[424px] lg:flex-shrink-0 lg:sticky lg:top-[24px] lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto">
            <PageHeader
              title={t('title')}
              description={t('descriptionShort')}
            />
            <AiItemsList
              items={financialModels}
              selectedId={selectedModel?.id ?? null}
              getItemTitle={getModelTitle}
              getItemSubtitle={getModelSubtitle}
              onSelect={setSelectedModel}
              isItemDisabled={(_item, index) => isFree && index > 0}
              tooltipMessage={t('tooltip.paidOnlyShort')}
            />

            {/* Create New Button */}
            <button
              onClick={() => { if (canCreateNew) { setEditingModel(null); setShowWizard(true); } }}
              disabled={!canCreateNew}
              onMouseEnter={() => !canCreateNew && setTooltipVisible({ id: 'create-sidebar' })}
              onMouseLeave={() => setTooltipVisible(null)}
              className="relative"
            >
              <div className={`${canCreateNew
                ? 'bg-[#2197f1] hover:bg-[#1a7bc7]'
                : 'bg-[rgba(33,151,241,0.2)] cursor-not-allowed opacity-60'
              } rounded-[16px] px-[24px] py-[12px] transition-colors`}>
                <p className="font-segoe font-semibold text-[16px] leading-[24px] text-white text-center">
                  {t('newButton')}
                </p>
              </div>

              {tooltipVisible?.id === 'create-sidebar' && !canCreateNew && (
                <div className="absolute top-[-60px] left-1/2 -translate-x-1/2 bg-[rgba(13,13,13,0.8)] px-[24px] py-[12px] rounded-[16px] whitespace-nowrap z-10">
                  <p className="font-segoe text-[14px] leading-[20px] text-white text-center">
                    {t('tooltip.paidOnly')}
                  </p>
                </div>
              )}
            </button>

          </div>

          {/* Right Content */}
          {selectedModel && (
            <FinancialModelDetailPanel
              key={selectedModel.id}
              model={selectedModel}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={() => {
                setEditingModel(selectedModel);
                setShowWizard(true);
              }}
              onDelete={() => {
                setModelToDelete(selectedModel.id);
                setShowDeleteConfirm(true);
              }}
              onRequestAi={handleRequestAi}
              onExportPdf={handleExportPdf}
              onExportCsv={handleExportCsv}
              isAiLoading={isAiLoading}
            />
          )}
        </div>
      )}

      {/* AI Loading Overlay */}
      {isAiLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-[24px] p-[40px] max-w-[480px] w-full mx-[16px]">
            <GenerationProgress
              title={t('aiOverlay.title')}
              message={t('aiOverlay.message')}
              estimatedDuration={60}
            />
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setModelToDelete(null);
        }}
        onConfirm={() => modelToDelete && handleDelete(modelToDelete)}
        title={t('confirm.deleteTitle')}
        description={t('confirm.deleteDescription')}
        confirmText={t('confirm.deleteConfirm')}
        cancelText={t('confirm.deleteCancel')}
      />

      {/* Wizard Modal */}
      <FinancialModelWizard
        isOpen={showWizard}
        onClose={() => { setShowWizard(false); setEditingModel(null); }}
        onSubmit={handleCreateModel}
        projects={projects}
        isLoading={isCreating}
        initialData={editingModel ? { ...editingModel.input_data, projectId: editingModel.project } : undefined}
      />
    </>
  );
}
